import React, { useState } from "react";
import { IonAlert, IonApp, IonRouterOutlet, setupIonicReact } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { Redirect, Route } from "react-router-dom";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";

import "swiper/css";
import "swiper/css/autoplay";
import "swiper/css/navigation";
import "swiper/css/pagination";

import "react-spring-bottom-sheet/dist/style.css";

/* Theme variables */
import "./theme/variables.css";
import "./global.scss";

/* Application Pages And Components */
import Products from "./pages/products/Products";
import Wishlist from "./pages/wish-list/Wishlist";
import Cart from "./pages/cart/cart";
import ContactUs from "./pages/contacts/ContactUs";
import { MyAccountPage as MyAccount } from "./pages/my-account/my-account";
import ForgotPassword from "./pages/modals/forgot-password/ForgotPassword";
import Login from "./pages/modals/login/Login";
import Order from "./pages/order/order";
import MyOrders from "./pages/my-orders/my-orders";
import MyOrderDetail from "./pages/my-order-detail/my-order-detail";
import ProductDetail from "./pages/product-detail/product-detail";
import SubCategories from "./pages/subCategories/subCategories";
import { ResetPassword } from "./pages/modals/ResetPassword/ResetPassword";

/* Application Providers And Events For State Management And Communication Between Components */
import { AppContextProvider } from "./providers/Redux/Reducers/AppContext";
import { history } from "./providers/Redux/Reducers/history";
import { AppMetricaErrorBoundary } from "./providers/AppMetrica/AppMetricaErrorBoundary";

/* Native Plugins */

import { Tabs } from "@core/components";
import SearchMainCategories from "./pages/searchMainCategories/searchMainCategories";

import { ApolloProvider } from "@apollo/client";
import { client } from "./apollo";
import { ShippingAddressPage } from "./pages/shipping-address/ShippingAddressPage";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./config/query-client";
import { AuthProvider, useAuth } from "./contexts/auth-context";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { CustomerAddressesStateProvider } from "./providers/CustomerAddresses/CustomerAddressesStateContext";
import AppUrlListener from "./utils/components/AppUrlListener";
import { AuthBySms } from "./pages/authBySms/AuthBySms";
import { useTheme } from "./theme/useTheme";
import "react-toastify/dist/ReactToastify.css";
import { Flip, ToastContainer } from "react-toastify";

import { useConfig } from "./providers/config";
import UnavailableProductsModal from "./components/UnavailableProductsModal";
import PollNewUser from "./components/PollNewUserModal";
import Home3 from "./pages/home3";
import ThankYou from "./pages/thank-you/thank-you";

import {
  useAppTitle,
  useVersionCheck,
  useAddFirstAddress,
  useLocalStorageInit,
  useInitFirebaseNotifications,
  useInitCartRemindLocalNotifications,
  useAppInitialization,
  useCustomerSurveyCheck,
  useCustomerDeviceRegistration,
  useUpdateCartOnShippingZoneChangeOrMarketMode,
  useTaxiMasterShippingActions,
  useScreenOrientation,
  useInitializeProductSubscriptions,
  useSetActiveModuleOnInit,
  useAppLiveUpdates,
} from "./hooks";

import {
  CategoryParhato2,
  HomeParhato2,
  MyOrdersParhato2,
  SettingsParhato2,
  CartParhato2,
  MyAccountParhato2,
  ProductShelfParhato2,
  AuthParhato2,
  SearchMainCategoriesParhato2,
  PromoPageParhato2,
  ThankYouPageParhato2,
  SubCategoriesParhato2,
  WishlistParhato2,
  FormalitiesPage,
  InviteFriendPage,
  MyOrderDetailPage,
  RateOrder,
  RecipesParhato2,
  NotificationsParhato2,
  OffertaParhato2,
  SharedCartParhato2,
  MySharedCartsParhato2,
} from "./pages/parhato2";
import { ProductDetailFortime } from "./pages/4time";

import { CartProgressButton } from "./parhato2/src/entities";
import RecipeCard from "./pages/parhato2/recipes/recipesCard";
import ToggleActiveModule from "./components/ToggleActiveModule";
import { useTranslation } from "react-i18next";
import { Portal } from "@headlessui/react";
import { MyOrderDetailFortime } from "./pages/4time";
import { PageName } from "@core/types";
import { useInitReferralFromClipboard } from "./providers/referrals/useInitReferralFromClipboard";
import ProductDetailPortal from "./components/ProductDetailPortal";
import { Capacitor } from "@capacitor/core";
import { QueryBoundaries } from "./parhato2/src/entities/ErrorBoundary";
import { YMaps } from "react-yandex-maps";
import "dayjs/locale/ru";
import MaxWidthContainer from "./components/UI/MaxWidthContainer/MaxWidthContainer";
import OrderChatPage from "./pages/parhato2/order-chat";
import { ChatUnreadSubscription } from "./providers/Chat";
import { useBackButton } from "./hooks/useBackButton";

dayjs.locale("ru");
dayjs.extend(duration);

setupIonicReact({
  mode: "ios",
  animated: Capacitor.getPlatform() !== "web",
});

const codeBaseVersion = process.env.REACT_APP_PARHATO_CODE_VERSION!;

const App: React.FC = () => {
  return (
    <ApolloProvider client={client}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AppContextProvider>
            <AppMetricaErrorBoundary>
              <YMaps
                query={{
                  load: "package.full",
                  apikey: process.env.REACT_APP_YANDEX_MAP_API_KEY,
                }}
              >
                <CustomerAddressesStateProvider>
                  <IonicApp />
                </CustomerAddressesStateProvider>
              </YMaps>
            </AppMetricaErrorBoundary>
          </AppContextProvider>
        </AuthProvider>
      </QueryClientProvider>
      <ToastContainer
        className={"toastContainer"}
        toastClassName={"toastToast"}
        bodyClassName={"toastBody"}
        draggable={false}
        closeButton={false}
        closeOnClick
        pauseOnHover
        pauseOnFocusLoss
        autoClose={1000}
        transition={Flip}
        newestOnTop={false}
        position="top-right"
      />
    </ApolloProvider>
  );
};

const pagesMap: Record<string, Partial<Record<PageName, any>>> = {
  home: {
    common: Home3,
    parhato2: HomeParhato2,
  },
  myOrders: {
    parhato2: MyOrdersParhato2,
    common: MyOrders,
  },
  searchMainCategories: {
    parhato2: SearchMainCategoriesParhato2,
    common: SearchMainCategories,
  },
  cart: {
    common: Cart,
    parhato2: CartParhato2,
  },
  account: {
    common: MyAccount,
    parhato2: MyAccountParhato2,
  },
  auth: {
    common: AuthBySms,
    parhato2: AuthParhato2,
  },
  thankYou: {
    common: ThankYou,
    parhato2: ThankYouPageParhato2,
  },
  subCategories: {
    common: SubCategories,
    parhato2: SubCategoriesParhato2,
  },
  orderDetail: {
    common: MyOrderDetail,
    parhato2: MyOrderDetailPage,
    fortime: MyOrderDetailFortime,
  },
  wishlist: {
    common: Wishlist,
    parhato2: WishlistParhato2,
  },
  productDetail: {
    common: ProductDetail,
    fortime: ProductDetailFortime,
  },
  notifications: {
    parhato2: NotificationsParhato2,
  },
} as const;

const AppRoutes = () => {
  const config = useConfig();
  const pages = config.pages;

  const {
    myOrders,
    home,
    searchMainCategories,
    cart,
    account,
    auth,
    thankYou,
    subCategories,
    orderDetail,
    wishlist,
    productDetail,
  } = pagesMap;

  const HomeMapPage = home[pages.homePageName as PageName];
  const AuthMapPage = auth[pages.authPageName as PageName];
  const CartMapPage = cart[pages.cartPageName as PageName];
  const ThankYouMapPage = thankYou[pages.thankYouPageName as PageName];
  const MyOrdersMapPage = myOrders[pages.myOrdersPageName as PageName];
  const MyAccountMapPage = account[pages.myAccountPageName as PageName];
  const WishlistMapPage = wishlist[pages.wishlistPageName as PageName];
  const SubCategoriesMapPage = subCategories[pages.subCategoriesPageName as PageName];
  const ProductDetailMapPage = productDetail[pages.productDetailPageName as PageName];
  const NotificationsMapPage = pagesMap.notifications.parhato2;

  const SearchMainCategoriesMapPage = searchMainCategories[pages.searchMainCategoriesPageName as PageName];
  const OrderDetailMapPage = orderDetail[pages.orderDetailPageName as PageName];

  return (
    <QueryBoundaries>
      <IonRouterOutlet id="main">
        <Route exact path="/">
          <Redirect to="/home3" />
        </Route>
        <Route exact path="/home">
          <Redirect to="/home3" />
        </Route>

        <Route exact path="/cart" component={CartMapPage} />
        <Route exact path="/shared-cart/:id" component={SharedCartParhato2} />
        <Route exact path="/my-shared-carts" component={MySharedCartsParhato2} />
        <Route exact path="/product-detail/:id" component={ProductDetailMapPage} />

        <Route exact path="/home3" component={HomeMapPage} />
        <Route exact path="/offerta" component={OffertaParhato2} />

        <Route exact path="/categories/:id/subcategories" component={SubCategoriesMapPage} />

        <Route exact path="/products/:id/:name/:type" component={Products} />
        <Route exact path="/category-detail/:id" component={CategoryParhato2} />

        <Route exact path="/wish-list" component={WishlistMapPage} />

        <Route exact path="/contact-us" component={ContactUs} />
        <Route exact path="/my-account" component={MyAccountMapPage} />
        <Route exact path="/thank-you/:orderId?" component={ThankYouMapPage} />
        <Route exact path="/settings" component={SettingsParhato2} />
        <Route exact path="/product-shelf/:id" component={ProductShelfParhato2} />

        <Route exact path="/formalities" component={FormalitiesPage} />
        <Route exact path="/invite-friend" component={InviteFriendPage} />

        <Route exact path="/shipping-address" component={ShippingAddressPage} />

        <Route exact path="/forgot-password" component={ForgotPassword} />
        <Route exact path="/reset-password" component={ResetPassword} />
        <Route exact path="/auth" component={AuthMapPage} />
        <Route exact path="/login/:name" component={Login} />
        <Route exact path="/promo/:id" component={PromoPageParhato2} />
        <Route exact path="/invite-friend-page" component={ThankYouMapPage} />
        <Route exact path="/rate-order" component={RateOrder} />
        <Route exact path="/orders/:orderId/chat" component={OrderChatPage} />

        <Route exact path="/search-main-categories" component={SearchMainCategoriesMapPage} />

        <Route exact path="/order" component={Order} />
        <Route exact path="/my-orders" component={MyOrdersMapPage} />
        <Route exact path="/recipes" component={RecipesParhato2} />
        <Route exact path="/recipe/:id" component={RecipeCard} />
        <Route exact path="/notifications" component={NotificationsMapPage} />

        <Route exact path="/my-order-detail/:id" component={OrderDetailMapPage} />
      </IonRouterOutlet>
    </QueryBoundaries>
  );
};

const AppListeners: React.FC<{
  setShowDisconnectedAlert: (value: boolean) => void;
  setShowConnectingAlert: (value: boolean) => void;
}> = ({ setShowDisconnectedAlert, setShowConnectingAlert }) => {
  useTheme();
  useAppTitle();
  useBackButton();
  useVersionCheck();
  useAppLiveUpdates();
  useAddFirstAddress();
  useLocalStorageInit();
  useScreenOrientation();
  useCustomerSurveyCheck();
  useSetActiveModuleOnInit();
  useInitFirebaseNotifications();
  useInitReferralFromClipboard();
  useCustomerDeviceRegistration();
  useInitializeProductSubscriptions();
  useInitCartRemindLocalNotifications();
  useUpdateCartOnShippingZoneChangeOrMarketMode();
  useTaxiMasterShippingActions({ enableRecalc: true });
  useAppInitialization(setShowDisconnectedAlert, setShowConnectingAlert);

  return null;
};

const IonicApp = () => {
  const { t } = useTranslation();
  const config = useConfig();
  const { user } = useAuth();
  const [showConnectingAlert, setShowConnectingAlert] = useState(false);
  const [showDisconnectedAlert, setShowDisconnectedAlert] = useState(false);
  const isNotCartPage = !history.location.pathname.includes("cart");

  const showUnavailableProductsModal =
    config.isUnavailableProductsEnabled && config.showUnavailableProductsModal && isNotCartPage;

  const showCartProgressButton = config.haveTabs && config.isCartProgressButtonEnabled;

  return (
    <IonApp>
      <IonAlert
        isOpen={showDisconnectedAlert}
        onDidDismiss={() => setShowDisconnectedAlert(false)}
        header={t("Disconnected")}
        message={t("Please Connect to the Internet!")}
        buttons={["OK"]}
      />
      <IonAlert
        isOpen={showConnectingAlert}
        onDidDismiss={() => setShowConnectingAlert(false)}
        header={t("Connected")}
        message={t("Network connected Reloading Data")}
        buttons={["OK"]}
      />
      <IonReactRouter history={history}>
        <AppListeners
          setShowConnectingAlert={setShowConnectingAlert}
          setShowDisconnectedAlert={setShowDisconnectedAlert}
        />
        <AppUrlListener />
        {!!config.isOrderChatEnabled && <ChatUnreadSubscription />}
        <Tabs />
        <PollNewUser />
        <ProductDetailPortal />

        {showCartProgressButton && (
          <div className="fixed bottom-[1.375rem] left-0 right-0 z-[1000] pointer-events-none">
            <MaxWidthContainer xlContainer={true} classList="flex justify-end">
              <CartProgressButton />
            </MaxWidthContainer>
          </div>
        )}

        {config.isToggleSwitchModuleEnabled && (
          <ToggleActiveModule isViewDescription={false} isViewPopover={false} isFixedButtonWithAlert />
        )}

        {showUnavailableProductsModal && <UnavailableProductsModal />}
        <AppRoutes />
      </IonReactRouter>
      <Portal>
        <span className="fixed bottom-0 right-[10px] text-[11px] text-gray-200 z-[99999999]">
          v{codeBaseVersion}.{user?.id}
        </span>
      </Portal>
    </IonApp>
  );
};

export default App;
